import { Badge, Button, Dialog, IconButton, Input, Select, VisuallyHidden } from '@artosera/ui';
import {
  CONFIDENTIALITES,
  formatDate,
  formatOctets,
  TYPES_DOCUMENT,
  type Confidentialite,
  type TypeDocument,
} from '@artosera/shared';
import { ArrowLeft, ChevronLeft, ChevronRight, Download, Lock, Plus } from 'lucide-react';
import type { JSX } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import {
  estErreurStepUpRequis,
  useArchiverDocument,
  useDeposerVersionDocument,
  useDocument,
  useJournalDocument,
  useModifierDocument,
  useTelechargerDocument,
  type FichierCoffre,
} from '../../api/coffre.js';
import { estApiError } from '../../api/errors.js';
import { useEquipe } from '../../api/equipe.js';
import { galerieALaction } from '../../auth/sujet.js';
import { Field } from '../Field.js';
import { useShellContext } from '../shell/context.js';
import { teinteDeLaConfidentialite } from './confidentialiteUi.js';
import { joursRestantsEcheance, teinteDeLEcheance } from './echeanceUi.js';
import { RattachementPastille } from './RattachementPastille.js';
import { RechercheRattachement } from './RechercheRattachement.js';
import { StepUpDialog } from './StepUpDialog.js';
import { FeuilleDocument } from './FeuilleDocument.js';
import type { RattachementLu } from './useRattachementResolu.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import styles from './DocumentDetailPage.module.css';

/**
 * Fiche doc — page entière (3.3, écran 4). Métadonnées éditables,
 * rattachements, versions (dépose), téléchargement journalisé (§ 18.5, avec
 * step-up si confidentiel), archivage, journal des consultations (§ 18.6).
 */
export function DocumentDetailPage(): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'coffre', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const { id } = useParams<{ id: string }>();
  const { galerieSlug, galerie } = useShellContext();

  const fiche = useDocument(galerieSlug, id ?? null);
  const modifier = useModifierDocument(galerieSlug);
  const deposerVersion = useDeposerVersionDocument(galerieSlug);
  const telecharger = useTelechargerDocument(galerieSlug);
  const archiver = useArchiverDocument(galerieSlug);
  const equipe = useEquipe(galerieSlug);

  const [journalPage, setJournalPage] = useState(1);
  const journal = useJournalDocument(galerieSlug, id ?? null, { page: journalPage, parPage: 20 });

  const [modeEdition, setModeEdition] = useState(false);
  const [titre, setTitre] = useState('');
  const [type, setType] = useState<TypeDocument>('autre');
  const [confidentialite, setConfidentialite] = useState<Confidentialite>('equipe');
  const [echeanceLe, setEcheanceLe] = useState('');
  const [rappelsJours, setRappelsJours] = useState('');
  const [etiquettes, setEtiquettes] = useState('');
  const [rattachements, setRattachements] = useState<readonly RattachementLu[]>([]);
  const [erreurEdition, setErreurEdition] = useState<string | null>(null);

  const [erreurTelechargement, setErreurTelechargement] = useState<string | null>(null);
  const [archivageOuvert, setArchivageOuvert] = useState(false);
  const [erreurArchivage, setErreurArchivage] = useState<string | null>(null);

  const [stepUpOuvert, setStepUpOuvert] = useState(false);
  const [actionEnAttente, setActionEnAttente] = useState<(() => void) | null>(null);

  const versionRef = useRef<HTMLInputElement>(null);

  if (fiche.isLoading) {
    return <p className={styles.vide}>{t('commun:chargement')}</p>;
  }
  if (fiche.isError || !fiche.data) {
    return (
      <p className={styles.vide} role="alert">
        {estApiError(fiche.error) ? fiche.error.message : t('commun:erreurReseau')}
      </p>
    );
  }

  const doc = fiche.data.document;
  const nomsParCompte = new Map((equipe.data?.membres ?? []).map((m) => [m.compteId, m.nom]));
  const joursRestants = doc.echeanceLe !== null ? joursRestantsEcheance(doc.echeanceLe) : null;

  const peutModifier = galerieALaction(galerie, 'documents:write');
  const peutArchiver = galerieALaction(galerie, 'documents:archive');
  const peutVoirJournal = galerieALaction(galerie, 'document-access:read');

  function ouvrirEdition(): void {
    setTitre(doc.titre);
    setType(doc.type);
    setConfidentialite(doc.confidentialite);
    setEcheanceLe(doc.echeanceLe ? doc.echeanceLe.slice(0, 10) : '');
    setRappelsJours(doc.rappelsJours.join(','));
    setEtiquettes(doc.etiquettes.join(', '));
    setRattachements(doc.rattachements);
    setErreurEdition(null);
    setModeEdition(true);
  }

  async function enregistrer(): Promise<void> {
    setErreurEdition(null);
    try {
      await modifier.mutateAsync({
        id: doc.id,
        corps: {
          titre: titre.trim(),
          type,
          confidentialite,
          echeanceLe: echeanceLe || null,
          rappelsJours: rappelsJours
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v.length > 0)
            .map(Number),
          etiquettes: etiquettes
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v.length > 0),
          rattachements,
        },
      });
      setModeEdition(false);
    } catch (error) {
      if (estErreurStepUpRequis(error)) {
        setActionEnAttente(() => () => void enregistrer());
        setStepUpOuvert(true);
        return;
      }
      setErreurEdition(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  async function telechargerFichier(version?: string): Promise<void> {
    setErreurTelechargement(null);
    try {
      const reponse = await telecharger.mutateAsync({ id: doc.id, version });
      ouvrirUrlSignee(reponse.telechargement.url);
    } catch (error) {
      if (estErreurStepUpRequis(error)) {
        setActionEnAttente(() => () => void telechargerFichier(version));
        setStepUpOuvert(true);
        return;
      }
      setErreurTelechargement(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  async function confirmerArchivage(): Promise<void> {
    setErreurArchivage(null);
    try {
      await archiver.mutateAsync(doc.id);
      setArchivageOuvert(false);
    } catch (error) {
      if (estErreurStepUpRequis(error)) {
        setActionEnAttente(() => () => void confirmerArchivage());
        setStepUpOuvert(true);
        return;
      }
      setErreurArchivage(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  function deposerNouvelleVersion(candidat: File | undefined): void {
    if (!candidat) return;
    deposerVersion.mutate(
      { id: doc.id, fichier: candidat },
      {
        onError: (error: unknown) => {
          if (estErreurStepUpRequis(error)) {
            setActionEnAttente(() => () => deposerNouvelleVersion(candidat));
            setStepUpOuvert(true);
          }
        },
      },
    );
  }

  const versionsAffichees: (FichierCoffre & { courante: boolean })[] = [
    { ...doc.fichier, courante: true },
    ...[...doc.versions].reverse().map((version) => ({ ...version, courante: false })),
  ];

  return (
    <div className={styles.enveloppe}>
      <Link className={styles.retour} to={`/${galerieSlug}/coffre`}>
        <ArrowLeft size={16} strokeWidth={1.5} aria-hidden />
        {t('regie:coffre.fiche.retour')}
      </Link>

      <div className={styles.entete}>
        <FeuilleDocument
          nomFichier={doc.fichier.nom}
          confidentiel={doc.confidentialite === 'confidentiel'}
          archive={doc.archive}
          taille="lg"
        />
        <div className={styles.enteteTitre}>
          <h1 className={styles.titre}>{doc.titre}</h1>
          <div className={styles.badges}>
            <Badge tone="neutral">{t(`coffre:types.${doc.type}`)}</Badge>
            <Badge tone={teinteDeLaConfidentialite(doc.confidentialite)}>
              {doc.confidentialite === 'confidentiel' ? <Lock size={11} aria-hidden /> : null}
              {t(`coffre:confidentialite.${doc.confidentialite}`)}
            </Badge>
            {doc.archive ? <Badge tone="withdrawn">{t('regie:coffre.liste.archive')}</Badge> : null}
            {joursRestants !== null ? (
              <Badge tone={teinteDeLEcheance(joursRestants)}>
                {joursRestants < 0
                  ? t('coffre:echeances.passee', { jours: Math.abs(joursRestants) })
                  : t('coffre:echeances.dans', { jours: joursRestants })}
              </Badge>
            ) : null}
          </div>

          <div className={styles.gestes}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void telechargerFichier()}
              loading={telecharger.isPending}
            >
              <Download size={16} strokeWidth={1.5} aria-hidden />
              {t('regie:coffre.fiche.telecharger')}
            </Button>
            {peutModifier && !modeEdition ? (
              <Button variant="secondary" size="sm" onClick={ouvrirEdition}>
                {t('commun:actions.modifier')}
              </Button>
            ) : null}
            {peutArchiver && !doc.archive ? (
              <Button variant="danger" size="sm" onClick={() => setArchivageOuvert(true)}>
                {t('regie:coffre.fiche.archiver')}
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {erreurTelechargement ? (
        <p className={styles.erreur} role="alert">
          {erreurTelechargement}
        </p>
      ) : null}

      <div className={styles.grille}>
        <section className={styles.section}>
          <h2 className={styles.titreSection}>{t('regie:coffre.fiche.metadonneesTitre')}</h2>
          {modeEdition ? (
            <form
              className={styles.formulaireEdition}
              onSubmit={(event) => event.preventDefault()}
              noValidate
            >
              <Field label={t('regie:coffre.nouveauDocument.titreChamp')} htmlFor="doc-titre">
                <Input
                  id="doc-titre"
                  required
                  value={titre}
                  onChange={(e) => setTitre(e.target.value)}
                />
              </Field>
              <div className={styles.grille2}>
                <Field label={t('regie:coffre.nouveauDocument.typeChamp')} htmlFor="doc-type">
                  <Select
                    id="doc-type"
                    aria-label={t('regie:coffre.nouveauDocument.typeChamp')}
                    value={type}
                    onValueChange={(v) => setType(v as TypeDocument)}
                    options={TYPES_DOCUMENT.map((code) => ({
                      value: code,
                      label: t(`coffre:types.${code}`),
                    }))}
                  />
                </Field>
                <Field
                  label={t('regie:coffre.nouveauDocument.confidentialiteChamp')}
                  htmlFor="doc-confidentialite"
                >
                  <Select
                    id="doc-confidentialite"
                    aria-label={t('regie:coffre.nouveauDocument.confidentialiteChamp')}
                    value={confidentialite}
                    onValueChange={(v) => setConfidentialite(v as Confidentialite)}
                    options={CONFIDENTIALITES.map((code) => ({
                      value: code,
                      label: t(`coffre:confidentialite.${code}`),
                    }))}
                  />
                </Field>
              </div>
              <Field
                label={t('regie:coffre.nouveauDocument.rattachementsChamp')}
                htmlFor="doc-rattachements"
              >
                <RechercheRattachement
                  galerieSlug={galerieSlug}
                  valeurs={rattachements}
                  onChanger={setRattachements}
                />
              </Field>
              <div className={styles.grille2}>
                <Field
                  label={t('regie:coffre.nouveauDocument.echeanceChamp')}
                  htmlFor="doc-echeance"
                >
                  <Input
                    id="doc-echeance"
                    type="date"
                    mono
                    value={echeanceLe}
                    onChange={(e) => setEcheanceLe(e.target.value)}
                  />
                </Field>
                <Field label={t('regie:coffre.nouveauDocument.rappelsChamp')} htmlFor="doc-rappels">
                  <Input
                    id="doc-rappels"
                    mono
                    disabled={!echeanceLe}
                    placeholder="30,7,1"
                    value={rappelsJours}
                    onChange={(e) => setRappelsJours(e.target.value)}
                  />
                </Field>
              </div>
              <Field
                label={t('regie:coffre.nouveauDocument.etiquettesChamp')}
                htmlFor="doc-etiquettes"
              >
                <Input
                  id="doc-etiquettes"
                  value={etiquettes}
                  onChange={(e) => setEtiquettes(e.target.value)}
                />
              </Field>
              {erreurEdition ? (
                <p className={styles.erreur} role="alert">
                  {erreurEdition}
                </p>
              ) : null}
              <div className={styles.actionsFormulaire}>
                <Button variant="ghost" onClick={() => setModeEdition(false)}>
                  {t('commun:actions.annuler')}
                </Button>
                <Button onClick={() => void enregistrer()} loading={modifier.isPending}>
                  {t('commun:actions.enregistrer')}
                </Button>
              </div>
            </form>
          ) : (
            <dl className={styles.metaGrille}>
              <div className={styles.metaLigne}>
                <dt>{t('regie:coffre.liste.colonneRattachements')}</dt>
                <dd className={styles.pastilles}>
                  {doc.rattachements.length === 0
                    ? '—'
                    : doc.rattachements.map((rattachement) => (
                        <RattachementPastille
                          key={`${rattachement.type}:${rattachement.id}`}
                          galerieSlug={galerieSlug}
                          rattachement={rattachement}
                        />
                      ))}
                </dd>
              </div>
              <div className={styles.metaLigne}>
                <dt>{t('coffre:echeances.titre')}</dt>
                <dd>
                  {doc.echeanceLe
                    ? formatDate(new Date(doc.echeanceLe), langue, 'long')
                    : t('regie:coffre.fiche.sansEcheance')}
                </dd>
              </div>
              <div className={styles.metaLigne}>
                <dt>{t('regie:coffre.nouveauDocument.rappelsChamp')}</dt>
                <dd>{doc.rappelsJours.length > 0 ? doc.rappelsJours.join(', ') : '—'}</dd>
              </div>
              <div className={styles.metaLigne}>
                <dt>{t('regie:coffre.nouveauDocument.etiquettesChamp')}</dt>
                <dd>
                  {doc.etiquettes.length > 0 ? (
                    <span className={styles.etiquettes}>
                      {doc.etiquettes.map((etiquette) => (
                        <Badge key={etiquette} tone="neutral">
                          {etiquette}
                        </Badge>
                      ))}
                    </span>
                  ) : (
                    '—'
                  )}
                </dd>
              </div>
              <div className={styles.metaLigne}>
                <dt>{t('regie:coffre.liste.colonneDepot')}</dt>
                <dd>
                  {formatDate(new Date(doc.fichier.depose), langue, 'long')}
                  {doc.fichier.deposePar
                    ? ` — ${nomsParCompte.get(doc.fichier.deposePar) ?? doc.fichier.deposePar}`
                    : ''}
                </dd>
              </div>
            </dl>
          )}
        </section>

        <section className={styles.section}>
          <div className={styles.enteteSection}>
            <h2 className={styles.titreSection}>{t('regie:coffre.fiche.versionsTitre')}</h2>
            {peutModifier && !doc.archive ? (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => versionRef.current?.click()}
                  loading={deposerVersion.isPending}
                >
                  <Plus size={16} strokeWidth={1.5} aria-hidden />
                  {t('regie:coffre.fiche.nouvelleVersion')}
                </Button>
                <input
                  ref={versionRef}
                  type="file"
                  accept="application/pdf,image/*"
                  aria-label={t('regie:coffre.fiche.nouvelleVersion')}
                  hidden
                  onChange={(event) => {
                    deposerNouvelleVersion(event.target.files?.[0]);
                    event.target.value = '';
                  }}
                />
              </>
            ) : null}
          </div>
          <div className={styles.tableauEnveloppe}>
            <table className={`${styles.tableau} ${styles.versions}`}>
              <thead>
                <tr>
                  <th scope="col">{t('regie:coffre.fiche.colonneFichier')}</th>
                  <th scope="col">{t('regie:coffre.fiche.colonneTaille')}</th>
                  <th scope="col">{t('regie:coffre.liste.colonneDepot')}</th>
                  <th scope="col">
                    <VisuallyHidden>{t('regie:coffre.fiche.telecharger')}</VisuallyHidden>
                  </th>
                </tr>
              </thead>
              <tbody>
                {versionsAffichees.map((version) => (
                  <tr key={version.cle} data-courante={version.courante ? 'oui' : undefined}>
                    <td className={styles.celFichier}>
                      <span className={styles.nomFichier}>{version.nom}</span>
                      {version.courante ? (
                        <Badge tone="accent" className={styles.badgeCourante}>
                          {t('regie:coffre.fiche.versionCourante')}
                        </Badge>
                      ) : null}
                    </td>
                    <td className={styles.celTaille}>{formatOctets(version.octets, langue)}</td>
                    <td className={styles.celDepot}>
                      {formatDate(new Date(version.depose), langue, 'court')}
                      {version.deposePar
                        ? ` — ${nomsParCompte.get(version.deposePar) ?? version.deposePar}`
                        : ''}
                    </td>
                    <td className={styles.celAction}>
                      <IconButton
                        variant="outline"
                        icon={<Download aria-hidden />}
                        aria-label={t('regie:coffre.fiche.telechargerVersion', {
                          nom: version.nom,
                        })}
                        size="sm"
                        onClick={() =>
                          void telechargerFichier(version.courante ? undefined : version.cle)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {peutVoirJournal ? (
        <section className={styles.section}>
          <h2 className={styles.titreSection}>{t('coffre:journal.titre')}</h2>
          <p className={styles.aide}>{t('coffre:journal.conservation')}</p>
          {journal.isLoading ? (
            <p className={styles.vide}>{t('commun:chargement')}</p>
          ) : journal.isError || !journal.data ? (
            <p className={styles.vide} role="alert">
              {estApiError(journal.error) ? journal.error.message : t('commun:erreurReseau')}
            </p>
          ) : journal.data.journal.length === 0 ? (
            <p className={styles.videJournal}>{t('regie:coffre.fiche.journalVide')}</p>
          ) : (
            <>
              <div className={styles.tableauEnveloppe}>
                <table className={`${styles.tableau} ${styles.journal}`}>
                  <thead>
                    <tr>
                      <th scope="col">{t('regie:coffre.fiche.colonneQui')}</th>
                      <th scope="col">{t('regie:coffre.fiche.colonneQuoi')}</th>
                      <th scope="col">{t('regie:coffre.fiche.colonneQuand')}</th>
                      <th scope="col">{t('regie:coffre.fiche.colonneAdresse')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {journal.data.journal.map((entree) => (
                      <tr key={entree.id}>
                        <td className={styles.celQui}>
                          {entree.compteEmail ?? t('regie:coffre.fiche.jetonApi')}
                        </td>
                        <td data-libelle={t('regie:coffre.fiche.colonneQuoi')}>
                          <span className={styles.action} data-action={entree.action}>
                            {t(`coffre:journal.${entree.action}`)}
                          </span>
                        </td>
                        <td data-libelle={t('regie:coffre.fiche.colonneQuand')}>
                          {formatDate(new Date(entree.date), langue, 'long')}
                        </td>
                        <td
                          className={styles.adresse}
                          data-libelle={t('regie:coffre.fiche.colonneAdresse')}
                        >
                          {entree.adresse}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {journal.data.pagination.pages > 1 ? (
                <div className={styles.pagination}>
                  <IconButton
                    variant="outline"
                    icon={<ChevronLeft aria-hidden />}
                    aria-label={t('regie:coffre.liste.pagination.precedent')}
                    size="sm"
                    disabled={journalPage <= 1}
                    onClick={() => setJournalPage((v) => Math.max(1, v - 1))}
                  />
                  <span>
                    {t('regie:coffre.liste.pagination.page', {
                      page: journal.data.pagination.page,
                      pages: journal.data.pagination.pages,
                    })}
                  </span>
                  <IconButton
                    variant="outline"
                    icon={<ChevronRight aria-hidden />}
                    aria-label={t('regie:coffre.liste.pagination.suivant')}
                    size="sm"
                    disabled={journalPage >= journal.data.pagination.pages}
                    onClick={() =>
                      setJournalPage((v) => Math.min(journal.data!.pagination.pages, v + 1))
                    }
                  />
                </div>
              ) : null}
            </>
          )}
        </section>
      ) : null}

      <Dialog
        open={archivageOuvert}
        onOpenChange={setArchivageOuvert}
        title={t('regie:coffre.fiche.archiverConfirmerTitre')}
        description={t('regie:coffre.fiche.archiverConfirmerTexte')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setArchivageOuvert(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              variant="danger"
              loading={archiver.isPending}
              onClick={() => void confirmerArchivage()}
            >
              {t('regie:coffre.fiche.archiver')}
            </Button>
          </>
        }
      >
        {erreurArchivage ? (
          <p className={styles.erreur} role="alert">
            {erreurArchivage}
          </p>
        ) : null}
      </Dialog>

      <StepUpDialog
        open={stepUpOuvert}
        onOpenChange={setStepUpOuvert}
        onVerifie={() => {
          const action = actionEnAttente;
          setActionEnAttente(null);
          action?.();
        }}
      />
    </div>
  );
}
